import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  KeyRound,
  User,
  Briefcase,
} from 'lucide-react';
import {
  createStaffSchema,
  ROLES,
  ALL_ROLE_CODES,
  type ApiError,
  type CreateStaffInput,
} from '@tracker/shared';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEAM_LEADER: 'Team Leader',
  USER: 'User',
};

function SectionHeader({
  icon,
  title,
  optional,
}: {
  icon: React.ReactNode;
  title: string;
  optional?: boolean;
}) {
  return (
    <CardHeader className="border-b border-border/40 pb-3">
      <CardTitle className="flex items-center gap-2 text-base font-semibold text-primary">
        {icon}
        {title}
        {optional && (
          <span className="ml-auto text-xs font-normal text-muted-foreground">Optional</span>
        )}
      </CardTitle>
    </CardHeader>
  );
}

export function StaffNewPage() {
  const navigate = useNavigate();

  const { register, handleSubmit, formState, control } = useForm<CreateStaffInput>({
    resolver: zodResolver(createStaffSchema),
    defaultValues: {
      role_codes: [ROLES.USER],
    },
  });

  const create = useMutation({
    mutationFn: (input: CreateStaffInput) => usersService.create(input),
    onSuccess: (res) => {
      toast.success('Staff created. Default modules assigned per role.');
      navigate(`/hrms/staff/${res.id}?tab=access`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function onSubmit(v: CreateStaffInput) {
    create.mutate(v);
  }

  const { errors } = formState;

  return (
    <div className="space-y-5">
      <Link
        to="/hrms/staff/list"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to staff list
      </Link>

      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">New Staff</h1>
        <p className="text-sm text-muted-foreground">
          Role default modules are auto-granted on save. Tweak per-user visibility on the Access
          tab after.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* ── Section 1: Account & Security ── */}
        <Card>
          <SectionHeader icon={<KeyRound className="h-4 w-4" />} title="Account & Security" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="employee_code">Employee Code</Label>
              <Input id="employee_code" placeholder="e.g. EMP001" {...register('employee_code')} />
              {errors.employee_code && (
                <p className="text-xs text-destructive">{errors.employee_code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="john@example.com" {...register('email')} />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Initial Password</Label>
              <Input id="password" type="password" placeholder="Min. 8 characters" {...register('password')} />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2: Name & Contact ── */}
        <Card>
          <SectionHeader icon={<User className="h-4 w-4" />} title="Name & Contact" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" placeholder="John" {...register('first_name')} />
              {errors.first_name && (
                <p className="text-xs text-destructive">{errors.first_name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" placeholder="Doe" {...register('last_name')} />
              {errors.last_name && (
                <p className="text-xs text-destructive">{errors.last_name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" placeholder="1234567890" {...register('phone')} />
              {errors.phone && (
                <p className="text-xs text-destructive">{errors.phone.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 3: Roles ── */}
        <Card>
          <SectionHeader icon={<Briefcase className="h-4 w-4" />} title="Roles" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Role(s)</Label>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <Controller
                  name="role_codes"
                  control={control}
                  render={({ field }) => (
                    <>
                      {ALL_ROLE_CODES.map((code) => (
                        <label
                          key={code}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <Checkbox
                            checked={field.value?.includes(code) ?? false}
                            onChange={(e) => {
                              const current = field.value ?? [];
                              field.onChange(
                                e.target.checked
                                  ? [...current, code]
                                  : current.filter((c) => c !== code),
                              );
                            }}
                          />
                          {ROLE_LABELS[code] ?? code}
                        </label>
                      ))}
                    </>
                  )}
                />
              </div>
              {errors.role_codes && (
                <p className="text-xs text-destructive">{errors.role_codes.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Role default modules are auto-granted. Adjust on the Access tab after creation.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pb-2">
          <Button type="button" variant="outline" onClick={() => navigate('/hrms/staff/list')}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Staff'}
          </Button>
        </div>
      </form>
    </div>
  );
}
